import { useEffect, useState } from "react";
import { Loader2, X, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import client from "@/lib/api";

export function TwoFactorModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    client
      .get("/auth/totp/status")
      .then((r) => setStatus(r.data))
      .catch(() => toast.error("Failed to load 2FA status"));
  }, []);

  async function startSetup() {
    setBusy(true);
    try {
      const r = await client.post("/auth/totp/setup/init");
      setSetup(r.data);
    } catch {
      toast.error("Failed to start setup");
    } finally {
      setBusy(false);
    }
  }

  async function confirmSetup(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await client.post("/auth/totp/setup/confirm", { code });
      setRecoveryCodes(r.data.recovery_codes);
      setSetup(null);
      setStatus({ enabled: true, recovery_codes_left: r.data.recovery_codes.length });
      setCode("");
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  async function disable(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/auth/totp/disable", { code });
      setStatus({ enabled: false, recovery_codes_left: 0 });
      setDisabling(false);
      setCode("");
      toast.success("Two-factor authentication disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      data-testid="twofactor-modal"
    >
      <div className="bg-white w-full max-w-md max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-overline uppercase text-[#A9927D] font-medium mb-1">
              Security
            </div>
            <div className="font-display text-3xl text-[#1F1E1D] leading-none">Two-Factor Login</div>
          </div>
          <button onClick={onClose} data-testid="twofactor-close-btn" className="text-[#8A8782] hover:text-[#1F1E1D]">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {!status ? (
          <div className="flex items-center gap-2 text-sm text-[#5C5A56]">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Loading…
          </div>
        ) : recoveryCodes ? (
          <div data-testid="recovery-codes-panel">
            <p className="text-sm text-[#5C5A56] mb-4">
              2FA is on. Save these one-time recovery codes somewhere safe (password manager or print them).
              Each works once if you ever lose your phone.
            </p>
            <div className="bg-[#F3F1ED] p-4 grid grid-cols-2 gap-2 mb-4">
              {recoveryCodes.map((c) => (
                <div key={c} className="font-mono text-sm text-[#1F1E1D]">{c}</div>
              ))}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(recoveryCodes.join("\n"));
                toast.success("Copied to clipboard");
              }}
              data-testid="copy-recovery-codes-btn"
              className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#2F2E2E] border-b border-[#2F2E2E] pb-1 font-medium"
            >
              <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy all
            </button>
          </div>
        ) : setup ? (
          <form onSubmit={confirmSetup} data-testid="twofactor-setup-panel">
            <p className="text-sm text-[#5C5A56] mb-4">
              Scan this QR code with <strong>Google Authenticator</strong>, then enter the 6-digit code to confirm.
            </p>
            <div className="flex justify-center mb-3 bg-white p-4 border border-[#E5E3DB]">
              <QRCodeSVG value={setup.otpauth_uri} size={180} />
            </div>
            <p className="text-[11px] text-[#8A8782] break-all mb-4">
              Can't scan? Enter manually: <span className="font-mono">{setup.secret}</span>
            </p>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              data-testid="twofactor-confirm-input"
              className="w-full border-b border-[#D1CEC2] bg-transparent text-2xl tracking-[0.3em] text-center py-3 focus:border-[#2F2E2E] outline-none mb-4"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="twofactor-confirm-btn"
              className="w-full bg-[#2F2E2E] hover:bg-[#454341] text-white px-6 py-3.5 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-60"
            >
              Confirm & Enable
            </button>
          </form>
        ) : status.enabled ? (
          <div data-testid="twofactor-enabled-panel">
            <div className="inline-flex items-center gap-2 text-sm text-[#2E7D32] mb-3">
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> 2FA is enabled
            </div>
            <p className="text-sm text-[#5C5A56] mb-6">
              New devices are asked for a Google Authenticator code at sign-in. Devices you've verified stay trusted for 90 days.
              Recovery codes remaining: <strong>{status.recovery_codes_left}</strong>.
            </p>
            {disabling ? (
              <form onSubmit={disable}>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code or recovery code"
                  required
                  data-testid="twofactor-disable-input"
                  className="w-full border-b border-[#D1CEC2] bg-transparent text-lg py-3 focus:border-[#2F2E2E] outline-none mb-4"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="twofactor-disable-confirm-btn"
                  className="w-full border border-[#8A3D3D] text-[#8A3D3D] hover:bg-[#8A3D3D] hover:text-white px-6 py-3 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-60"
                >
                  Confirm disable
                </button>
              </form>
            ) : (
              <button
                onClick={() => setDisabling(true)}
                data-testid="twofactor-disable-btn"
                className="text-[11px] tracking-overline uppercase text-[#8A3D3D] border-b border-[#8A3D3D] pb-1 font-medium"
              >
                Disable 2FA
              </button>
            )}
          </div>
        ) : (
          <div data-testid="twofactor-off-panel">
            <p className="text-sm text-[#5C5A56] mb-6">
              Add a second layer of security: new devices will need a 6-digit code from Google Authenticator
              as well as your password. Trusted devices skip the code for 90 days.
            </p>
            <button
              onClick={startSetup}
              disabled={busy}
              data-testid="twofactor-enable-btn"
              className="w-full bg-[#2F2E2E] hover:bg-[#454341] text-white px-6 py-3.5 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-60"
            >
              {busy ? "Preparing…" : "Enable 2FA"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
